import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Public as SystemIcon,
  FolderSpecial as PortfolioIcon,
  Factory as PlantIcon,
  ExpandMore as ExpandIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useApp, ScopeTypes } from '../../context/AppContext';
import { getPortfolios, getActivePortfolio } from '../../services/portfolioService';

export default function ScopeSwitcher() {
  const { state, actions } = useApp();
  const [anchorEl, setAnchorEl] = useState(null);
  const [portfolios, setPortfolios] = useState([]);

  // Load portfolios
  useEffect(() => {
    setPortfolios(getPortfolios());
  }, []);

  // Initialize scope from active portfolio on mount
  useEffect(() => {
    const activePortfolio = getActivePortfolio();
    if (activePortfolio && state.scope.type === ScopeTypes.SYSTEM) {
      actions.setScope(ScopeTypes.PORTFOLIO, activePortfolio.id, activePortfolio.name);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    // Refresh portfolios when opening
    setPortfolios(getPortfolios());
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectSystem = () => {
    actions.setScope(ScopeTypes.SYSTEM);
    handleClose();
  };

  const handleSelectPortfolio = (portfolio) => {
    actions.setScope(ScopeTypes.PORTFOLIO, portfolio.id, portfolio.name);
    handleClose();
  };

  // Get current scope label and icon
  const getScopeDisplay = () => {
    switch (state.scope.type) {
      case ScopeTypes.PORTFOLIO:
        return {
          label: state.scope.portfolioName || 'Portfolio',
          icon: <PortfolioIcon sx={{ fontSize: 18 }} />,
          color: '#00ff88',
        };
      case ScopeTypes.PLANT:
        return {
          label: state.scope.plantName || 'Plant',
          icon: <PlantIcon sx={{ fontSize: 18 }} />,
          color: '#f1c40f',
        };
      default:
        return {
          label: 'System',
          icon: <SystemIcon sx={{ fontSize: 18 }} />,
          color: '#00d4ff',
        };
    }
  };

  const display = getScopeDisplay();

  return (
    <>
      <Tooltip
        title="Choose what data to view: entire market (System) or just your assets (Portfolio). Create portfolios to track your own plants' performance."
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: '#1a1a2e',
              border: '1px solid rgba(255,255,255,0.2)',
              maxWidth: 280,
              p: 1.5,
            },
          },
          arrow: { sx: { color: '#1a1a2e' } },
        }}
      >
        <Button
          onClick={handleOpen}
          size="small"
          sx={{
            px: 2,
            py: 0.75,
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: `1px solid ${display.color}40`,
            color: 'white',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: display.color,
            },
          }}
          endIcon={<ExpandIcon sx={{ fontSize: 18 }} />}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: display.color }}>{display.icon}</Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {display.label}
            </Typography>
          </Box>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(26, 26, 46, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            minWidth: 240,
            mt: 1,
          },
        }}
      >
        <Typography
          variant="overline"
          sx={{ px: 2, py: 1, color: 'rgba(255,255,255,0.5)', display: 'block' }}
        >
          View Scope
        </Typography>

        {/* System scope */}
        <MenuItem
          onClick={handleSelectSystem}
          sx={{
            py: 1.5,
            '&:hover': { backgroundColor: 'rgba(0, 212, 255, 0.1)' },
          }}
        >
          <ListItemIcon>
            <SystemIcon sx={{ color: '#00d4ff' }} />
          </ListItemIcon>
          <ListItemText
            primary="System"
            secondary="View all market data"
            primaryTypographyProps={{ color: 'white', fontWeight: 500 }}
            secondaryTypographyProps={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}
          />
          {state.scope.type === ScopeTypes.SYSTEM && (
            <CheckIcon sx={{ color: '#00d4ff', ml: 1 }} />
          )}
        </MenuItem>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1 }} />

        <Typography
          variant="overline"
          sx={{ px: 2, py: 0.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}
        >
          Portfolios
        </Typography>

        {portfolios.length === 0 ? (
          <MenuItem disabled sx={{ py: 1.5 }}>
            <ListItemText
              primary="No portfolios"
              secondary="Create one in Settings"
              primaryTypographyProps={{ color: 'rgba(255,255,255,0.5)' }}
              secondaryTypographyProps={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}
            />
          </MenuItem>
        ) : (
          portfolios.map((portfolio) => (
            <MenuItem
              key={portfolio.id}
              onClick={() => handleSelectPortfolio(portfolio)}
              sx={{
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(0, 255, 136, 0.1)' },
              }}
            >
              <ListItemIcon>
                <PortfolioIcon sx={{ color: '#00ff88' }} />
              </ListItemIcon>
              <ListItemText
                primary={portfolio.name}
                secondary={`${portfolio.plants?.length || 0} plants`}
                primaryTypographyProps={{ color: 'white', fontWeight: 500 }}
                secondaryTypographyProps={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}
              />
              {state.scope.type === ScopeTypes.PORTFOLIO && state.scope.portfolioId === portfolio.id && (
                <CheckIcon sx={{ color: '#00ff88', ml: 1 }} />
              )}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
